/**
 * Step 2 of sign-in: the TOTP code (or a backup code).
 *
 * The only credential this screen holds is the short-lived `mfa_token` minted
 * by `/auth/login`. It is not a session — no cookie exists until
 * `/auth/2fa/verify` accepts a code — so if the server rejects the token as
 * expired there is nothing to retry and the user goes back to the password
 * step. That is what `onExpired` is for.
 */
import { useState } from 'react'
import type { FormEvent } from 'react'

import { ApiError, api } from '../api/client'
import type { TwoFactorResponse } from '../api/types'
import { useI18n } from '../i18n'
import { AuthCard, Field, FormError, INPUT, SubmitButton, describeAuthError } from './LoginPage'
import type { AuthMessage } from './LoginPage'

/** `schemas.TwoFactorRequest` allows 6-10 characters: a 6-digit TOTP code or a
 *  10-digit single-use backup code. Anything shorter is a 422, so it is caught
 *  here rather than spent as a failed attempt against the 5-try lockout. */
const MIN_CODE = 6
const MAX_CODE = 10

export interface TwoFactorPageProps {
  mfaToken: string
  onVerified?: (result: TwoFactorResponse) => void
  /** The pending token is dead; the user must re-enter their password. */
  onExpired?: () => void
  onCancel?: () => void
}

export function TwoFactorPage({ mfaToken, onVerified, onExpired, onCancel }: TwoFactorPageProps) {
  const { t } = useI18n()
  const [code, setCode] = useState('')
  const [error, setError] = useState<AuthMessage | null>(null)
  const [pending, setPending] = useState(false)

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (pending) return
    if (code.length < MIN_CODE) {
      setError({ key: 'auth.twoFactor.codeTooShort', params: { length: MIN_CODE } })
      return
    }
    setPending(true)
    setError(null)
    try {
      const result = await api.post<TwoFactorResponse>('/auth/2fa/verify', {
        mfa_token: mfaToken,
        code,
      })
      onVerified?.(result)
    } catch (err) {
      if (err instanceof ApiError && err.status === 401 && isTokenDead(err.detail)) {
        onExpired?.()
        setError({ key: 'auth.twoFactor.expired' })
        return
      }
      setError(describeAuthError(err, 'mfa'))
      setCode('')
    } finally {
      setPending(false)
    }
  }

  return (
    <AuthCard
      // The card title is the product vocabulary itself, so it reuses the
      // `domain` key rather than translating 双重验证 a second time here.
      title={t('domain.twoFactor')}
      subtitle={t('auth.twoFactor.subtitle')}
      onSubmit={submit}
    >
      <Field
        id="mfa-code"
        label={t('auth.twoFactor.codeLabel')}
        hint={t('auth.twoFactor.codeHint')}
      >
        <input
          id="mfa-code"
          name="code"
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          autoFocus
          maxLength={MAX_CODE}
          value={code}
          // Authenticator apps display "123 456" and people paste it that way;
          // both TOTP and backup codes are digits, so anything else is noise.
          onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, MAX_CODE))}
          className={`${INPUT} font-mono tracking-[0.3em]`}
        />
      </Field>

      <FormError message={error && t(error.key, error.params)} />

      <SubmitButton pending={pending}>{t('auth.twoFactor.submit')}</SubmitButton>

      {onCancel && (
        <button
          type="button"
          onClick={onCancel}
          className="w-full text-center text-xs text-muted underline-offset-2 hover:underline"
        >
          {t('auth.twoFactor.useDifferentAccount')}
        </button>
      )}
    </AuthCard>
  )
}

/** `/auth/2fa/verify` answers 401 for both a bad code and a dead pending token;
 *  only the detail tells them apart, and only one of them is worth retrying. */
function isTokenDead(detail: string): boolean {
  return /expired|not configured/i.test(detail)
}
